import React, { useContext }from 'react';
import { withTheme } from '@ifit/themes/withTheme';
import { ThemeContext } from '@ifit/themes/ThemeContext';
import classNames from 'classnames';
import styles from './index.module.scss';
import Icon from '@ifit/react-icons/dist/components/icon'
import EXERCISE_SOLID from '@ifit/react-icons/dist/icons/EXERCISE_SOLID'
import { white, white24, shark, shark24, scienceBlue, mountainMeadow } from '@ifit/base-coat/js/colors';

export interface IProps {
  progress: number;
  icon?: Icon;
  themeClass?: string;
  className?: string;
  deviceType?: string;
  isVault?: boolean;
  diameter?: number;
}

const COLORS = {
  LIGHT: {
    iconColor: shark,
    incompleteProgressColor: shark24,
  },
  DARK: {
    iconColor: white,
    incompleteProgressColor: white24,
  },
  VAULT: {
    iconColor: shark,
    incompleteProgressColor: shark24,
  }
}

const ProgressCircle = ({
  progress = 0,
  icon = EXERCISE_SOLID,
  themeClass,
  className,
  deviceType = 'tablet',
  diameter,
  isVault,
  ...rest
}: IProps) => {

  let iconSize: number;
  let progressBarWidth: number;

  if (progress > 100) {
    progress = 100;
  }

  if (progress < 0) {
    progress = 0;
  }
  
  if (deviceType === 'mobile' && !diameter) {
    diameter = 60;
    iconSize = 15.75;
    progressBarWidth = 7.18;
  } else if (deviceType === 'mobile' || diameter <= 60) {
    iconSize = 15.75;
    progressBarWidth = 7.18;
  } else if (isVault && !diameter) {
    diameter = 88;
    iconSize = 24;
    progressBarWidth = 8;
  } else if (isVault || diameter <= 88) {
    iconSize = 24;
    progressBarWidth = 8;
  } else if (diameter <= 72) {
    iconSize = 19.2;
    progressBarWidth = 8;
  } else if (!diameter) {
    diameter = 96;
    iconSize = 26.4;
    progressBarWidth = 8.8;
  } else if (diameter >= 89) {
    iconSize = 26.4;
    progressBarWidth = 8.8;
  } else if (diameter <= 40) {
    diameter = 40;
    iconSize = 15.75;
    progressBarWidth = 7.18;
  }

  const { theme } = useContext(ThemeContext);
  const iconColor = COLORS[theme].iconColor;
  const incompleteProgressColor = COLORS[theme].incompleteProgressColor;
  const completeProgressColor = progress === 0 ? incompleteProgressColor : 'url(#linear)';
  
  const width = diameter;
  const height = diameter;
  const isProgressOver50 = progress > 50;
  const startingProgress = 0;
  const startingDegrees = 280;

  const pi = Math.PI;
  let [radius, midpoint, cx, cy] = Array(6).fill(diameter / 2);

  const innerCircleRadius = radius - progressBarWidth;
  const outerCircleRadius = radius;
  const midlineCircleRadius = radius - (progressBarWidth / 2);
  const progressLineCircleRadius = progressBarWidth / 2;

  const progressAngleDegrees = ((progress / 100) * 360) + startingDegrees;
  const startingProgressAngleDegrees = ((startingProgress / 100) * 360) + startingDegrees;
  const firstMaskArcEndAngleDegrees = ((50 / 100) * 360) + startingDegrees;

  const progressAngleRadians = progressAngleDegrees * (pi / 180);
  const startingProgressAngleRadians = startingProgressAngleDegrees * (pi / 180);
  const firstMaskArcEndAngleRadians = firstMaskArcEndAngleDegrees * (pi / 180);

  const startingProgressMidlineCirclePointX = cx + midlineCircleRadius * Math.cos(startingProgressAngleRadians);
  const startingProgressMidlineCirclePointY = cy + midlineCircleRadius * Math.sin(startingProgressAngleRadians);
  const startingProgressOuterCirclePointX = cx + outerCircleRadius * Math.cos(startingProgressAngleRadians);
  const startingProgressOuterCirclePointY = cy + outerCircleRadius * Math.sin(startingProgressAngleRadians);

  const progressMidlineCirclePointX = cx + midlineCircleRadius * Math.cos(progressAngleRadians);
  const progressMidlineCirclePointY = cy + midlineCircleRadius * Math.sin(progressAngleRadians);
  const progressOuterCirclePointX = cx + outerCircleRadius * Math.cos(progressAngleRadians);
  const progressOuterCirclePointY = cy + outerCircleRadius * Math.sin(progressAngleRadians);

  const firstMaskArcEndPointX = cx + outerCircleRadius * Math.cos(firstMaskArcEndAngleRadians);
  const firstMaskArcEndPointY = cy + outerCircleRadius * Math.sin(firstMaskArcEndAngleRadians);

  const firstMaskArcEndX = isProgressOver50 ? firstMaskArcEndPointX : progressOuterCirclePointX;
  const firstMaskArcEndY = isProgressOver50 ? firstMaskArcEndPointY : progressOuterCirclePointY;
  const lastMaskArcStartX = isProgressOver50 ? firstMaskArcEndPointX : startingProgressOuterCirclePointX;
  const lastMaskArcStartY = isProgressOver50 ? firstMaskArcEndPointY : startingProgressOuterCirclePointY;

  const sharedMask = (fill: string) => (
    <>
      <path d={`M ${startingProgressOuterCirclePointX} ${startingProgressOuterCirclePointY} A ${radius},${radius} 0 0,1 ${firstMaskArcEndX},${firstMaskArcEndY} M ${lastMaskArcStartX} ${lastMaskArcStartY} A ${radius},${radius} 0 0,1 ${progressOuterCirclePointX},${progressOuterCirclePointY} L ${midpoint},${midpoint}`} fill={fill}></path>
      <circle cx={startingProgressMidlineCirclePointX} cy={startingProgressMidlineCirclePointY} r={progressLineCircleRadius} fill={fill} data-testid={`starting-progress-circle-${fill}`}/>  
      <circle cx={progressMidlineCirclePointX} cy={progressMidlineCirclePointY} r={progressLineCircleRadius} fill={fill} data-testid={`ending-progress-circle-${fill}`}/> 
      <circle cx={cx} cy={cy} r={innerCircleRadius} fill="black"/>
    </>
  )
  
  return (
    <div
      className={classNames(
        styles['mp-progress-circle'],
        themeClass,
        className,
      )}
      data-testid="mp-progress-circle"
      {...rest}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height} fill="none" stroke="none" data-testid="progress-svg">
        <linearGradient id="linear" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor={mountainMeadow}/>
          <stop offset="100%"  stopColor={scienceBlue}/>
        </linearGradient>
        <mask id="progressMask">
          {sharedMask('white')}
        </mask>
        <circle cx={cx} cy={cy} r={radius} fill={completeProgressColor} mask="url(#progressMask)"/>
        <foreignObject x="0" y="0" width={width} height={height} stroke="none">
        <div className={styles.iconWrapper}>
          <Icon
            color={iconColor}
            icon={icon}
            size={iconSize}
            unitSize={'px'}
            data-testid="ifit-react-icon"
          />
        </div>
        </foreignObject>
        <mask id="incompleteProgressMask">
          <circle cx={cx} cy={cy} r={radius} fill="white"/>
          {sharedMask('black')}
        </mask>
        <circle cx={cx} cy={cy} r={radius} fill={incompleteProgressColor} mask="url(#incompleteProgressMask)"/>
      </svg>
    </div>
  );
};

export default withTheme(ProgressCircle);
